import InfoRounded from '@mui/icons-material/InfoRounded';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { SwitchProps } from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { RHFPercentField } from '@openx/components-core/lib/NumberField/RHFPercentField.tsx';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { RHFTextFieldWithAdornment } from '@openx/components-core/lib/TextField/RHFTextFieldWithAdornment.tsx';
import { TextWithInfo } from '@openx/components-core/lib/TextWithInfo/TextWithInfo.tsx';
import { currencySymbol } from '@openx/utils/lib/priceFormat.ts';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { organizationFields } from '../../../../constants.ts';
import type { OrganizationFormData } from '../../../../types.ts';

type RevenueShareFieldsProps = {
  labels: Record<string, string>;
  isRevenueShareEnabled: boolean;
  onRevenueShareSwitchChange: SwitchProps['onChange'];
};

export const RevenueShareFields = ({
  isRevenueShareEnabled,
  labels,
  onRevenueShareSwitchChange,
}: RevenueShareFieldsProps) => {
  const { t } = useTranslation();
  const observableExchangeAccountPartnerId = useWatch<
    OrganizationFormData,
    typeof organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID
  >({
    name: organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID,
  });

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <RHFSwitchField
          checked={isRevenueShareEnabled}
          data-test="is-ox-revenue-share-enabled"
          disabled={!observableExchangeAccountPartnerId}
          label={t('Enable OpenX Revenue Share Partner')}
          name={organizationFields.IS_OPENX_REVENUE_SHARE_ENABLED}
          onChange={onRevenueShareSwitchChange}
        />

        <Tooltip placement="right" title={t('Curated Deal Fees Data is required for Revenue Share Partners')}>
          <IconButton data-test="tooltip-icon" size="small">
            <InfoRounded />
          </IconButton>
        </Tooltip>
      </Stack>

      {isRevenueShareEnabled && (
        <IndentedSections>
          <Typography sx={{ mb: 0.5 }} variant="h3">
            {t('Organization Max Fees')}
          </Typography>

          <RHFPercentField
            dataTest="max-percentage-of-media"
            formControlProps={{ margin: 'dense' }}
            label={`${labels[organizationFields.MAX_SHARE]}`}
            name={organizationFields.MAX_SHARE}
            placeholder={`${t('Enter ')}${labels[organizationFields.MAX_SHARE]}`}
          />

          <RHFTextFieldWithAdornment
            dataTest="max-fixed-cpm"
            formControlProps={{ margin: 'dense' }}
            label={`${labels[organizationFields.MAX_CPM_CAP]}`}
            name={organizationFields.MAX_CPM_CAP}
            placeholder={t('Enter Max Fixed CPM e. g. 1.00')}
            startAdornment={currencySymbol('USD')}
          />

          <TextWithInfo
            containerSx={{ mb: 0.5, mt: 2.5 }}
            tooltip={t('This is the default fee that will be automatically suggested when creating a deal.')}
            variant="h3"
          >
            {t('Organization Default Fees')}
          </TextWithInfo>

          <RHFPercentField
            dataTest="default-percentage-of-media"
            formControlProps={{ margin: 'dense' }}
            label={labels[organizationFields.DEFAULT_SHARE]}
            name={organizationFields.DEFAULT_SHARE}
            placeholder={t('Enter {field}', { field: labels[organizationFields.DEFAULT_SHARE] })}
          />

          <RHFTextFieldWithAdornment
            dataTest="default-fixed-cpm"
            formControlProps={{ margin: 'dense' }}
            label={labels[organizationFields.DEFAULT_CPM_CAP]}
            name={organizationFields.DEFAULT_CPM_CAP}
            placeholder={t('Enter {field}', { field: labels[organizationFields.DEFAULT_CPM_CAP] })}
            startAdornment={currencySymbol('USD')}
          />

          <RHFTextField
            dataTest="default-fees-description"
            fullWidth
            label={labels[organizationFields.DEFAULT_FEES_DESCRIPTION]}
            margin="dense"
            multiline
            name={organizationFields.DEFAULT_FEES_DESCRIPTION}
            placeholder={t('Enter description')}
            slotProps={{ formHelperText: { component: 'div' }, htmlInput: { maxLength: 255 } }}
            rows={4}
          />

          <Typography sx={{ mb: 0.5, mt: 2.5 }} variant="h3">
            {t('OpenX Platform Fee')}
          </Typography>

          <RHFPercentField
            dataTest="openx-percentage-of-media-for-ox-sourced-deals"
            formControlProps={{ margin: 'dense' }}
            label={labels[organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED]}
            name={organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED}
            placeholder={t('Enter {field}', {
              field: labels[organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED],
            })}
          />

          <RHFPercentField
            dataTest="openx-percentage-of-media-for-partner-sourced-deals"
            formControlProps={{ margin: 'dense' }}
            label={labels[organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED]}
            name={organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED}
            placeholder={t('Enter {field}', {
              field: labels[organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED],
            })}
          />
        </IndentedSections>
      )}
    </>
  );
};
